import React from 'react';
import { Controller } from 'react-hook-form';
import { REMINDER_TYPES, REMINDER_FREQUENCIES } from 'constants/reminders';
import { TIME_FORMATS } from 'constants/date';
import { Skeleton } from '../../layout/loading';
import DateField from '../DateField/DateField';
import TimeField from '../TimeField/TimeField';
import WeekDaySelector from '../WeekDaySelector/WeekDaySelector';
import MonthDaySelector from '../MonthDaySelector/MonthDaySelector';

export default function ScheduleField({
  control,
  type,
  frequency,
  loading = false,
  dateFormat,
  timeFormat,
}) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <Skeleton variant="text" className="w-24 mb-2" />
          <Skeleton variant="rectangular" className="h-9 w-full rounded-md" />
        </div>
      </div>
    );
  }

  const isAnnual = frequency === REMINDER_FREQUENCIES.ANNUAL;
  if (type === REMINDER_TYPES.ONE_TIME || isAnnual) {
    return (
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <Controller
          name="schedule.executionDate"
          control={control}
          rules={{ required: 'Date is required' }}
          render={({ field, fieldState: { error } }) => (
            <DateField
              label={isAnnual ? "Date & Time (Year ignored)" : "Date & Time"}
              showTime
              hideYear={isAnnual}
              dateFormat={dateFormat}
              timeFormat={timeFormat}
              error={error?.message}
              {...field}
            />
          )}
        />
      </div>
    );
  }

  // 2. Repeating (Daily, Weekly, Monthly)
  const isRecurringTimeBased = [
    REMINDER_FREQUENCIES.DAILY,
    REMINDER_FREQUENCIES.WEEKLY,
    REMINDER_FREQUENCIES.MONTHLY
  ].includes(frequency);

  if (isRecurringTimeBased) {
    return (
      <div className="space-y-6">
        {frequency === REMINDER_FREQUENCIES.WEEKLY && (
          <div className="w-full">
            <Controller
              name="schedule.daysOfWeek"
              control={control}
              rules={{ required: 'Select at least one day' }}
              render={({ field, fieldState: { error } }) => (
                <WeekDaySelector
                  label="Repeat on"
                  error={error?.message}
                  {...field}
                />
              )}
            />
          </div>
        )}

        {frequency === REMINDER_FREQUENCIES.MONTHLY && (
          <div className="w-full">
            <Controller
              name="schedule.daysOfMonth"
              control={control}
              rules={{ required: 'Select at least one day' }}
              render={({ field, fieldState: { error } }) => (
                <MonthDaySelector
                  label="Repeat on days"
                  error={error?.message}
                  {...field}
                />
              )}
            />
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Controller
            name="schedule.time"
            control={control}
            rules={{ required: 'Time is required' }}
            render={({ field, fieldState: { error } }) => (
              <TimeField
                label="Time"
                use12HourFormat={timeFormat === TIME_FORMATS.H12}
                error={error?.message}
                {...field}
              />
            )}
          />
        </div>
      </div>
    );
  }

  return null;
}
